import React, { createContext, useContext, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/auth';
import { dashboardService, DashboardCourse, DashboardStats, ProgressActivity, ScoreTrend } from '@/services/dashboardService';
import { queryKeys } from '@/hooks/queries/keys';

interface DashboardContextType {
  courses: DashboardCourse[];
  stats: DashboardStats | null;
  recentActivity: ProgressActivity[];
  scoreTrends: ScoreTrend[];
  isLoading: boolean;
  error: string | null;
  refreshDashboardData: () => void;
}

const DashboardContext = createContext<DashboardContextType | undefined>(undefined);

export const DashboardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();
  const userId = currentUser?.id;

  const query = useQuery({
    queryKey: queryKeys.student.dashboard(userId ?? ''),
    queryFn: () => dashboardService.getDashboardData(userId!),
    enabled: Boolean(userId),
    throwOnError: false,
  });

  const refreshDashboardData = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.student.dashboard(userId ?? '') });
  }, [queryClient, userId]);

  const value = {
    courses: query.data?.courses ?? [],
    stats: query.data?.stats ?? null,
    recentActivity: query.data?.recentActivity ?? [],
    scoreTrends: query.data?.scoreTrends ?? [],
    // Match previous semantics: "loading" until data arrives; never loading without a user.
    isLoading: Boolean(userId) && query.isPending,
    error: query.error ? (query.error instanceof Error ? query.error.message : 'Failed to load dashboard data') : null,
    refreshDashboardData,
  };

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
};

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (context === undefined) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }
  return context;
};
